import { Button, EButtonColorVariant, ELoaderSize, Loader } from '@outdoorsyco/bonfire';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { defaultLocale } from '@/config/locales';
import { ESearchFilters } from '@/constants/searchFilters';
import { EFilterMethod, setFormFilter } from '@/redux/modules/searchForm';
import { getCurrency } from '@/redux/selectors/currency';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
import { isHisotgramLoading } from '@/redux/selectors/search/pricing';
import { getSearchFormPriceFilter } from '@/redux/selectors/search/searchForm';
import { clamp } from '@/utility/numbers';

import PriceFilter from './PriceFilter';

interface IPriceFilterContainerProps {
  show: boolean;
  showConfirm?: boolean;
  onSubmit?: () => void;
  isInMoreFilter?: boolean;
}

export const PriceFilterContainer = ({
  show,
  showConfirm = true,
  onSubmit,
  isInMoreFilter = false,
}: IPriceFilterContainerProps) => {
  const intl = useIntl();
  const rawDispatch = useDispatch();
  const dispatch = debounce(rawDispatch, 32);
  const {
    [ESearchFilters.PRICE_MIN]: minPriceFromStore,
    [ESearchFilters.PRICE_MAX]: maxPriceFromStore,
  } = useSelector(getSearchFormPriceFilter);
  const {
    priceHistogram: { data: histogramValues, maxValue },
  } = useSelector(getSearchResultsMeta);
  const isHistogramLoading = useSelector(isHisotgramLoading);
  const prevIsLoading = useRef(isHistogramLoading);
  const initializedRef = useRef(false);

  const [prices, setPrices] = useState<[number, number]>([0, maxValue]);
  const currency = useSelector(getCurrency) || defaultLocale.base_currency;
  const { [ESearchFilters.PRICE_MIN]: pMin, [ESearchFilters.PRICE_MAX]: pMax } =
    useSelector(getQueryParams);

  const handlePriceChange = (min: number, max: number) => {
    const targetMin = clamp(min, 0, maxValue);
    const targetMax = clamp(max, 100, maxValue);

    setPrices([targetMin, targetMax]);
  };
  const handlePriceChangeCommitted = (min: number, max: number, fromInput = false) => {
    const targetMin = clamp(min, 0, maxValue);
    const targetMax = clamp(max, 100, maxValue);

    dispatch(
      setFormFilter({
        filters: {
          [ESearchFilters.PRICE_MIN]: targetMin === 0 ? undefined : targetMin,
          [ESearchFilters.PRICE_MAX]: targetMax === maxValue ? undefined : targetMax,
        },
        filterMethod: fromInput ? EFilterMethod.INPUT : EFilterMethod.SLIDER,
      }),
    );
  };

  // Handle price reset from store
  useEffect(() => {
    // when both are undefined, assume a reset
    if (typeof minPriceFromStore === 'undefined' && typeof maxPriceFromStore === 'undefined') {
      setPrices([0, maxValue]);
    } else if (minPriceFromStore !== undefined || maxPriceFromStore !== undefined) {
      // Handle partial updates
      const newMin = minPriceFromStore !== undefined ? minPriceFromStore : 0;
      const newMax = maxPriceFromStore !== undefined ? maxPriceFromStore : maxValue;
      setPrices([newMin, newMax]);
    }
  }, [minPriceFromStore, maxPriceFromStore, maxValue]);

  // Initialize from URL query params when histogram is loaded
  useEffect(() => {
    // Handle the case when histogram finishes loading - update prices once
    if (prevIsLoading.current && !isHistogramLoading && !initializedRef.current) {
      const targetMin = clamp(Number(pMin || minPriceFromStore || 0), 0, maxValue);
      const targetMax = clamp(Number(pMax || maxPriceFromStore || maxValue), 100, maxValue);
      dispatch(
        setFormFilter(
          {
            filters: {
              ...(pMin && {
                [ESearchFilters.PRICE_MIN]: targetMin,
              }),
              ...(pMax && {
                [ESearchFilters.PRICE_MAX]: targetMax,
              }),
            },
            filterMethod: null,
          },
          true,
        ),
      );
      setPrices([targetMin, targetMax]);
      initializedRef.current = true;
    }

    prevIsLoading.current = isHistogramLoading;
  }, [isHistogramLoading, pMin, pMax, dispatch, maxValue, maxPriceFromStore, minPriceFromStore]);

  // Sync with histogram values on first load
  useEffect(() => {
    if (histogramValues?.length && !initializedRef.current) {
      const targetMin = clamp(Number(pMin || 0), 0, maxValue);
      const targetMax = clamp(Number(pMax || maxValue), 100, maxValue);

      setPrices([targetMin, targetMax]);
      initializedRef.current = true;
    }
  }, [histogramValues, pMin, pMax, maxValue]);

  // Force reset values immediately in the UI when min/max are both undefined in More Filter context
  useEffect(() => {
    if (
      isInMoreFilter &&
      typeof minPriceFromStore === 'undefined' &&
      typeof maxPriceFromStore === 'undefined'
    ) {
      // Force immediate UI update without waiting for histogram
      setPrices([0, maxValue]);
    }
  }, [isInMoreFilter, minPriceFromStore, maxPriceFromStore, maxValue]);

  // Alternate Force reset values when queryValues  are both undefined in More Filter context but NOT in Store
  useEffect(() => {
    if (
      isInMoreFilter &&
      typeof minPriceFromStore !== 'undefined' &&
      typeof maxPriceFromStore !== 'undefined' &&
      pMin === undefined &&
      pMax === undefined
    ) {
      // Force immediate UI update without waiting for histogram
      setPrices([minPriceFromStore, maxPriceFromStore]);
    }
  }, [isInMoreFilter, minPriceFromStore, maxPriceFromStore, pMin, pMax]);

  return (
    <div className="pb-8 md:pb-0">
      {isHistogramLoading ? (
        <Loader className="flex items-center justify-center !w-full" size={ELoaderSize.Large} />
      ) : (
        <>
          <PriceFilter
            minValue={0}
            maxValue={maxValue}
            currency={currency}
            histogramValues={histogramValues}
            onChange={handlePriceChange}
            onChangeCommitted={handlePriceChangeCommitted}
            defaultValue={prices}
            show={show}
            isInMoreFilter={isInMoreFilter}
          />
          {showConfirm && (
            <Button
              className="mt-8 md:hidden"
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({ defaultMessage: 'Confirm', id: 'N2IrpM' })}
              fullWidth
              onClick={onSubmit}
            />
          )}
        </>
      )}
    </div>
  );
};
